import { Award, Target, Heart, Users } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { useLanguage } from '../context/LanguageContext';

const About = () => {
  const { language, isRTL } = useLanguage();

  const values = language === 'ar' ? [
    {
      icon: Award,
      title: 'التميز',
      description: 'نسعى للتميز في كل جانب من عملياتنا، من رعاية الماشية إلى تسليم المنتج النهائي.'
    },
    {
      icon: Heart,
      title: 'النزاهة',
      description: 'الشفافية والصدق يوجهان علاقاتنا مع العملاء والشركاء ومجتمعنا.'
    },
    {
      icon: Target,
      title: 'الجودة',
      description: 'معايير الجودة الصارمة تضمن أن كل منتج يلبي أعلى المعايير.'
    },
    {
      icon: Users,
      title: 'المجتمع',
      description: 'ندعم المجتمعات المحلية ونساهم في التنمية الزراعية المستدامة.'
    }
  ] : [
    {
      icon: Award,
      title: 'Excellence',
      description: 'We strive for excellence in every aspect of our operations, from livestock care to final product delivery.'
    },
    {
      icon: Heart,
      title: 'Integrity',
      description: 'Transparency and honesty guide our relationships with customers, partners, and our community.'
    },
    {
      icon: Target,
      title: 'Quality',
      description: 'Uncompromising quality standards ensure that every product meets the highest benchmarks.'
    },
    {
      icon: Users,
      title: 'Community',
      description: 'We support local communities and contribute to sustainable agricultural development.'
    }
  ];

  return (
    <div className="min-h-screen" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Hero */}
      <section className="bg-stone-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-6">
            {language === 'ar' ? 'عن إيليت ميت فارم' : 'About EliteMeat Farm'}
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            {language === 'ar' 
              ? 'بناء الثقة من خلال الجودة والسلامة والممارسات المستدامة'
              : 'Building trust through quality, safety, and sustainable practices'}
          </p>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className={isRTL ? 'md:order-2' : ''}>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                {language === 'ar' ? 'قصتنا' : 'Our Story'}
              </h2>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                {language === 'ar'
                  ? 'تأسست إيليت ميت فارم بمهمة بسيطة: تقديم منتجات لحوم عالية الجودة مع الحفاظ على أعلى معايير رعاية الحيوان والمسؤولية البيئية.'
                  : 'EliteMeat Farm was founded with a simple mission: to provide the finest quality meat products while maintaining the highest standards of animal welfare and environmental responsibility.'}
              </p>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                {language === 'ar'
                  ? 'ما بدأ كمزرعة عائلية صغيرة نما ليصبح موردًا موثوقًا يخدم تجار الجملة والتجزئة والموزعين في جميع أنحاء المنطقة. نجاحنا مبني على الالتزام الراسخ بالجودة ورضا العملاء.'
                  : 'What started as a small family farm has grown into a trusted supplier serving wholesalers, retailers, and distributors across the region. Our success is built on unwavering commitment to quality and customer satisfaction.'}
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                {language === 'ar'
                  ? 'اليوم، نواصل الابتكار والتوسع مع البقاء صادقين لقيمنا الأساسية المتمثلة في النزاهة والتميز والاستدامة.'
                  : 'Today, we continue to innovate and expand while staying true to our core values of integrity, excellence, and sustainability.'}
              </p>
            </div>
            <div className={isRTL ? 'md:order-1' : ''}>
              <img
                src="https://images.unsplash.com/photo-1560493676-04071c5f467b?w=800"
                alt={language === 'ar' ? 'مزرعتنا' : 'Our Farm'}
                className="rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <Card className="border-0 shadow-lg">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {language === 'ar' ? 'رسالتنا' : 'Our Mission'}
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {language === 'ar'
                    ? 'تقديم منتجات لحوم عالية الجودة ومصادر أخلاقية مع تقديم خدمة استثنائية لعملائنا. نحن ملتزمون بالممارسات المستدامة التي تفيد مجتمعنا وبيئتنا.'
                    : 'To deliver premium quality, ethically sourced meat products while providing exceptional service to our customers. We are committed to sustainable practices that benefit our community and environment.'}
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {language === 'ar' ? 'رؤيتنا' : 'Our Vision'}
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {language === 'ar'
                    ? 'أن نكون المورد الأكثر ثقة لمنتجات اللحوم الفاخرة، معروفين بالتزامنا بالجودة والاستدامة ورضا العملاء.'
                    : 'To be the most trusted supplier of premium meat products, recognized for our commitment to quality, sustainability, and customer satisfaction.'}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {language === 'ar' ? 'قيمنا الأساسية' : 'Our Core Values'}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {language === 'ar'
                ? 'المبادئ التي توجه كل ما نقوم به'
                : 'The principles that guide everything we do'}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <value.icon className="text-red-600" size={32} />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-red-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            {language === 'ar' ? 'مستعد للشراكة معنا؟' : 'Ready to Partner With Us?'}
          </h2>
          <p className="text-xl mb-8 opacity-90">
            {language === 'ar'
              ? 'تواصل معنا اليوم لمناقشة كيف يمكننا خدمة احتياجاتك من اللحوم.'
              : 'Contact us today to discuss how we can serve your meat product needs.'}
          </p>
          <a 
            href="/contact" 
            className="inline-block bg-white text-red-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
          >
            {language === 'ar' ? 'تواصل معنا' : 'Contact Us'}
          </a>
        </div>
      </section>
    </div>
  );
};

export default About; 